import { useState, useEffect } from "react";
import {
  AutoCompleteTextFieldWrap,
  Sensor,
  PopConfirm,
  SelectIcon,
  Title,
  QrCodeScanner,
} from "@/components";
import {
  Autocomplete,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { t } from "i18next";
import { object, string } from "yup";
import {
  loadingStore,
  useProjectStore,
  useSensorsStore,
  useAppStore,
} from "@/stores";
import { useNavigate, useParams } from "react-router";
import { LoadingKeys, routesDetails } from "@/constants";
import { ISensorQrDetailsRes, IBananaQrDetailsRes } from "@/models";
import { toast } from "react-toastify";
import { DeviceIcon, BananaSensor } from "@/components/icons";
import CloseIcon from "@mui/icons-material/Close";
import { green } from "@mui/material/colors";
import { color } from "html2canvas/dist/types/css/types/color";
import theme from "@/theme/index";

interface AddSensorQRProps {
  projectId: number;
  pourId: number;
  useNfc: boolean;
  sensorId: string|null;
  bananaId: string|null;
  needScanBanana: boolean;
  isSwitchDevice?: boolean;
  onFinish: (payload: {
    sensor_Id?: string;
    display_name?: string;
    banana_Id?: string;
    device_number?: number;
    is_exist?: boolean;
    }) => void;
  onError?: () => void;
}


const AddSensorQR = (
  {
  projectId,
  pourId,
  useNfc,
  sensorId,
  bananaId,
  needScanBanana,
  isSwitchDevice,
  onFinish,
  onError,
  }: AddSensorQRProps
) => {
  const [finshScan, setfinshScan] = useState<boolean>(false);
  const [is_device, setDeviceNumber] = useState<boolean>(false);
  const { getLoading } = loadingStore();
  const { currentUser } = useAppStore();
  const { getSensorIdFromQr, getBananaIdFromQr, checkLifeSign } =
    useSensorsStore();
  
  const [isDevicePopupOpen, setDevicePopupOpen] = useState(false);
  const [isBananaPopupOpen, setBananaPopupOpen] = useState(false);
  const closeDevicePopup = () => { 
    setDevicePopupOpen(false);
    setScanningError(null); // Reset the scanning error on close
  };
  const openBananaPopup = () => setBananaPopupOpen(true);
  const closeBananaPopup = () => {
    setBananaPopupOpen(false);
    setScanningError(null); // Reset the scanning error on close
  };

  const [readyToAdd, setReadyToAdd] = useState(false);
  const [sensorAssiginMsg, setsenSorAssiginMsg] = useState("");
  const [scanningError, setScanningError] = useState<string>("");
  const [bananaApiError, setBananaApiError] = useState(false);
  const [scanningColor, setScanningColor] = useState("#C8C8C8");
  const [isPopconfirmOpen, setIsPopconfirmOpen] = useState(false);
  const [platform, setPlatform] = useState("");
  const [bananaData, setBananaData] = useState<IBananaQrDetailsRes>();
  const [sensorData, setSensorData] = useState<ISensorQrDetailsRes>();
  // Detect Platform Utility
  function detectPlatform() {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
    if (/Android/i.test(ua)) return "Android";
    return "Other";
  }

  // Fetch Project Data
  useEffect(() => {
    setPlatform(detectPlatform());
  }, [projectId]);

  useEffect(() => {
    formik.setFieldValue("sensorId", sensorId);
  }, [sensorId]);

  useEffect(()=>{
    setDevicePopupOpen(!needScanBanana)
    setBananaPopupOpen(needScanBanana??false);
  },[needScanBanana])

  useEffect(()=>{
    console.log(`isDevicePopupOpen=${isDevicePopupOpen}`)
  },[isDevicePopupOpen])

  useEffect(()=>{
    formik.setFieldValue("bananaId", bananaId);
  },[bananaId])

  // Form Handling
  const formik = useFormik({
    initialValues: {
      name: "",
      sensorId: "",
      bananaId: "",
    },
    validationSchema: object({
      name: string().required(t("thisFieldIsMandatory")),
      sensorId: string().required(t("thisFieldIsMandatory")),
      bananaId:
         is_device
          ? string().required(t("thisFieldIsMandatory"))
          : undefined,
    }),
    onSubmit: async () => {
    }
  });

  const handleConfirmPopup = (field: string, data: any) => {
    if (field === "sensor") {
      formik.setFieldValue("name", data?.display_name);
      formik.setFieldValue("sensorId", data?.sensor_id);
      closeDevicePopup();
    } else if (field === "banana" && is_device) {
      formik.setFieldValue("bananaId", data?.banana_id);
    } 

    if (is_device) 
        openBananaPopup();
    
    onFinish({banana_Id: data?.banana_id})
    setScanningColor("#C8C8C8");
  };

  useEffect(() => {
    // Check if NFC should be disabled based on conditions
    if (!currentUser?.user?.use_nfc) {
      // Prevent NFC algorithm from running when NFC is disabled
      return;
    }
    let reader: NDEFReader | null = null;

    const connectToNFC = async () => {
      if (!("NDEFReader" in window)) {
        alert("Web NFC API is not supported in this browser.");
        return;
      }

      reader = new NDEFReader();
      try {
        await reader.scan();

        reader.onreadingerror = () => {
          setScanningError(
            "Error! Cannot read data from the NFC tag. Try a different one?"
          );
          setScanningColor("#C8C8C8"); // Gray color on error
        };

        reader.onreading = async (event: NDEFReadingEvent) => {
          const { records } = event.message;
          if (records.length > 1) {
            return;
          }
          const decoder = new TextDecoder(records[0].encoding || "utf-8");
          handleQrScan(decoder.decode(records[0].data.buffer as any),(!finshScan && needScanBanana));
        };
      } catch (error) {
        setScanningError("Error starting NFC scan. Please try again.");
        setScanningColor("#C8C8C8"); // Gray for scanning error
      }
    };

    if ((isDevicePopupOpen || isBananaPopupOpen) && platform === "Android") {
      connectToNFC();
    }

    return () => {
      if (reader) {
        reader.onreading = null;
        reader.onreadingerror = null;
        reader = null;
      }
    };
  }, [
    isDevicePopupOpen,
    isBananaPopupOpen,
    platform,
    currentUser?.user?.use_nfc,
    getBananaIdFromQr,
    getSensorIdFromQr,
  ]);

  const handleBananaScan = (
    itemUrl: string,
    scannedItemId: string
  ): boolean => {
    if (!scannedItemId.includes("-")) {
      setScanningError("Invalid Sensor data code");
      setScanningColor("#bc544b"); // Error color
      return false; // Return false if invalid action
    }
    // Fetch banana data based on the itemId (similar to NFC)
    getBananaIdFromQr(itemUrl, (data) => {
      setBananaApiError(false);
      setBananaData((prevState) => {
        const updatedData = {
          banana_id: scannedItemId,
        };
        // Trigger the function after the state update
        handleConfirmPopup("banana", updatedData);
        setfinshScan(true);
        return updatedData;
      });

      setScanningColor("#009999"); // Success color

      onFinish({banana_Id: scannedItemId})
      toast.success("Banana scanned successfully");
    }, () => {
      setBananaApiError(true);
      onError?.();
    });
    return true;
  };

  const handleDeviceScan = (
    itemUrl: string,
    scannedItemId: string
  ): boolean => {
    if (scannedItemId.includes("-")) {
      setScanningError("Invalid Device code");
      setScanningColor("#bc544b"); // Error color
      return false; // Return false if invalid action
    }
    if(itemUrl.toLowerCase().includes("h_") || itemUrl.toLowerCase().includes("b_")){
      setScanningError("Invalid Device code");
      setScanningColor("#bc544b"); // Error color
      return false; // Return false if invalid action
    }
    // Fetch sensor data based on the itemId (similar to NFC)
    getSensorIdFromQr(scannedItemId, (data) => {
      setSensorData(() => {
        const updatedData = {
          sensor_id: data?.sensor_id,
          display_name: data?.display_name || "",
          isexist: data?.isexist,
          device_number: data?.device_number,
        };
        setDeviceNumber(data?.device_number != null ? true : false);
        // Trigger the function after the state update
        handleConfirmPopup("sensor", updatedData);
        
        onFinish({sensor_Id: data?.sensor_id,
                device_number: data?.device_number,
                is_exist: data?.isexist,
                display_name: data?.display_name})
        return updatedData;
      });
      if(isSwitchDevice) {toast.success(t('deviceScanSuccessfully'))};
      setScanningColor("#009999"); // Success color
      closeDevicePopup();
      openBananaPopup();
    });
    return true;
  };

  const parseItemUrl = (itemUrl: string): string => {
    try {
      const pathParts = itemUrl.split("/");
      return pathParts[pathParts.length - 1]?.split('_').at(-1);
    } catch {
      return itemUrl;
    }
  };

  const handleQrScan = (scannedData: string,isBanana: boolean): boolean => {
    if (!scannedData) {
      setScanningError("No data found in the data code.");
      setScanningColor("#cda984"); // Yellow color for no data
      return false; // Return false if no data
    }
    try {
      const itemId = parseItemUrl(scannedData);
      if (!itemId) {
        setScanningError("Failed to parse data code.");
        setScanningColor("#bc544b"); // Error color
        return false;
      }
      if (!isBanana) return handleDeviceScan(scannedData, itemId);
      else if (isBanana) return handleBananaScan(scannedData, itemId);
    } catch (error) {
      setScanningError("Failed to parse data code.");
      setScanningColor("#bc544b"); // Error color
      console.log(error);
      return false; // Return false if parsing fails
    }
  };

  return (
    <>
        {!finshScan && !needScanBanana && 
        <>
            <Box>
            Device Scanning
            <IconButton
                edge="start"
                onClick={closeDevicePopup}
                aria-label="close"
                sx={{
                position: "absolute",
                left: 18,
                top: 8,
                color: "black",
                }}
            >
            </IconButton>
            </Box>
            <Box>
            {(
                <Box>
                <Box sx={{ minHeight: "24px", marginTop: "8px" }}>
                    {scanningError && (
                    <Box color="#bc544b">Error: {scanningError}</Box>
                    )}
                </Box>
                <Box sx={{ minHeight: "200px" }}>
                    <QrCodeScanner
                    isLoading={getLoading(LoadingKeys.GetSensorIdFromQr)}
                    onScan={handleQrScan}
                    isBanana={false}
                    />
                </Box>
                </Box>
            )}
            </Box>
        </>}

   
        {!finshScan && needScanBanana && 
        <>
            <Box>
                <Box>
                Sensor Scanning
                <IconButton
                    edge="start"
                    onClick={closeBananaPopup}
                    aria-label="close"
                    sx={{
                    position: "absolute",
                    left: 18,
                    top: 8,
                    color: "black",
                    }}
                >
                    
                </IconButton>
                </Box>
                <Box>
                {(
                    <Box>
                    <Box sx={{ minHeight: "24px", marginTop: "8px" }}>
                        {scanningError && (
                        <Box color="#bc544b">Error: {scanningError}</Box>
                        )}
                    </Box>
                    <Box sx={{ minHeight: "200px" }}>
                        <QrCodeScanner
                        isLoading={getLoading(LoadingKeys.GetBananaIdFromQr)}
                        onScan={handleQrScan}
                        isBanana={true}
                        errorText={bananaApiError ? t("bananaScanError") : undefined}
                        />
                    </Box>
                    </Box>
                )}
                </Box>
            </Box>
        </>}

        {finshScan && 
        <>
            <Box>
                <p style={{ color:theme.palette.primary.main }}>Finish Scanning</p>
                
            </Box>
        </>}
    </>
  );
};

export default AddSensorQR;
